import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useChatConfig } from '@/contexts/ChatConfigProvider';
import { aiCoreApi } from '@/services/aiCoreClient';
import { faqKeys } from '@/services/queryClient';
import { useBoundStore } from '@/store/store';

export function useFAQ() {
  const { chatId } = useChatConfig();
  const queryClient = useQueryClient();

  const {
    currentFAQId,
    childrenCount,
    isInFAQLoop,
    FAQHistory,
    setFAQ,
    setFAQHistory,
    goBack,
    setIsInFAQLoop,
    resetFAQLoop,
  } = useBoundStore(
    useShallow(state => ({
      currentFAQId: state.currentFAQId,
      childrenCount: state.childrenCount,
      isInFAQLoop: state.isInFAQLoop,
      FAQHistory: state.FAQHistory,
      setFAQ: state.setFAQ,
      setFAQHistory: state.setFAQHistory,
      goBack: state.goBack,
      setIsInFAQLoop: state.setIsInFAQLoop,
      resetFAQLoop: state.resetFAQLoop,
    })),
  );

  const navigateToFAQ = useCallback(
    async (id: string, childrenCount: number) => {
      setFAQ(id, childrenCount);
      setFAQHistory([...FAQHistory, id]);
    },
    [setFAQ, setFAQHistory, FAQHistory],
  );

  const navigateBack = useCallback(async () => {
    const { previousId, canGoBack } = goBack();

    if (canGoBack && previousId) {
      try {
        // Fetch the previous FAQ to get its children count
        const previousFAQs = await aiCoreApi.faqs.findAll({
          chatId,
          parentId: previousId,
        });

        // Update the store with the correct children count
        setFAQ(previousId, previousFAQs.length);

        // Invalidate the query to refetch with the new parentId
        queryClient.invalidateQueries({
          queryKey: [...faqKeys.chatLists(chatId), previousId],
        });

        return { success: true, previousId };
      } catch (error) {
        console.error('Error fetching previous FAQ:', error);
        // If we can't fetch the previous FAQ, just go back to root
        setFAQ(previousId, 0);

        return { success: false, error };
      }
    }

    return { success: false, error: 'Cannot go back' };
  }, [goBack, chatId, setFAQ, queryClient]);

  // If there is any history, we can go back
  const canGoBack = FAQHistory.length > 0;

  const resetFAQ = useCallback(() => {
    resetFAQLoop();
    queryClient.invalidateQueries({
      queryKey: [...faqKeys.chatLists(chatId)],
    });
  }, [resetFAQLoop, queryClient, chatId]);

  return {
    // State
    currentFAQId,
    childrenCount,
    isInFAQLoop,
    FAQHistory,
    canGoBack,

    // Actions
    navigateToFAQ,
    navigateBack,
    resetFAQ,
    setIsInFAQLoop,

    // Query helpers
    queryClient,
    chatId,
  };
}
